import React, { useRef, useState } from 'react';
// TODO: Replace with ECharts implementation
// import {
//   PieChart,
//   Pie,
//   Cell,
//   ResponsiveContainer,
//   Sector
// } from 'recharts';
import { 
  styled, 
  Box, 
  Typography, 
  useTheme, 
  IconButton, 
  Tooltip as MuiTooltip,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { exportChartToPng, handleChartExportToPdf } from '../../utils/chartUtils';

export interface GaugeProps {
  /** Title for the gauge */
  title: string;
  /** Current value */
  value: number;
  /** Minimum value for the scale */
  min?: number;
  /** Maximum value for the scale */
  max?: number;
  /** Optimal range start */
  optimalStart?: number;
  /** Optimal range end */
  optimalEnd?: number;
  /** Units label */
  unit?: string;
  /** Gauge size */
  size?: number;
  /** Colors for different ranges */
  colors?: {
    belowOptimal?: string;
    optimal?: string;
    aboveOptimal?: string;
  };
  /** Last updated timestamp */
  lastUpdated?: Date | null;
  /** Function to refresh data */
  onRefresh?: () => void;
  /** Loading state */
  isLoading?: boolean;
}

const GaugeContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  margin: theme.spacing(2, 0),
  position: 'relative',
}));

const GaugeHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const GaugeTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const GaugeValue = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.text.primary,
}));

const GaugeOptimalRange = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

const LastUpdatedText = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 255, 255, 0.7)',
  borderRadius: theme.shape.borderRadius,
  zIndex: 5,
}));

// Define a simple Sector component for the gauge
const Sector = ({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }: any) => {
  return (
    <path
      d={`M ${cx} ${cy} L ${cx + outerRadius} ${cy} A ${outerRadius} ${outerRadius} 0 0 1 ${cx} ${cy + outerRadius} Z`}
      fill={fill}
    />
  );
};

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

export const WaterQualityGauge: React.FC<GaugeProps> = ({
  title,
  value,
  min = 0,
  max = 100,
  optimalStart,
  optimalEnd,
  unit = '',
  size = 200,
  colors = {
    belowOptimal: '#f39c12',
    optimal: '#2ecc71',
    aboveOptimal: '#e74c3c',
  },
  lastUpdated,
  onRefresh,
  isLoading = false,
}) => {
  const theme = useTheme();
  const gaugeRef = useRef<HTMLDivElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);
  
  // Default optimal range if not provided
  const optStart = optimalStart !== undefined ? optimalStart : (max - min) * 0.25 + min;
  const optEnd = optimalEnd !== undefined ? optimalEnd : (max - min) * 0.75 + min;

  // Normalize value to percentage for the gauge
  const normalizedValue = Math.max(min, Math.min(max, value));
  const valuePercentage = ((normalizedValue - min) / (max - min)) * 100;
  
  // Determine color based on value
  let gaugeColor;
  if (normalizedValue < optStart) {
    gaugeColor = colors.belowOptimal;
  } else if (normalizedValue > optEnd) {
    gaugeColor = colors.aboveOptimal;
  } else {
    gaugeColor = colors.optimal;
  }
  
  // Create data for the gauge
  const gaugeData = [
    { value: valuePercentage, color: gaugeColor },
    { value: 100 - valuePercentage, color: theme.palette.grey[200] },
  ];
  
  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };
  
  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };
  
  const handleExportPng = () => {
    handleExportMenuClose();
    exportChartToPng(gaugeRef, title || 'water-quality-gauge');
  };
  
  const handleExportPdf = () => {
    handleExportMenuClose();
    handleChartExportToPdf(gaugeRef, [{ name: title, value: normalizedValue }], title || 'water-quality-gauge', title);
  };
  
  const formattedLastUpdated = lastUpdated 
    ? `Updated: ${lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}`
    : '';
  
  return (
    <GaugeContainer>
      <GaugeHeader>
        <GaugeTitle variant="h6">{title}</GaugeTitle>
        <ControlsContainer>
          {onRefresh && (
            <MuiTooltip title="Refresh data">
              <IconButton size="small" onClick={onRefresh} disabled={isLoading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          )}
          <MuiTooltip title="Export options">
            <IconButton size="small" onClick={handleExportMenuOpen}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
          <Menu
            anchorEl={exportMenuAnchor}
            open={exportMenuOpen}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={handleExportPng}>Export as PNG</MenuItem>
            <MenuItem onClick={handleExportPdf}>Export as PDF</MenuItem>
          </Menu>
        </ControlsContainer>
      </GaugeHeader>
      
      <Box sx={{ width: size, height: size / 1.5, position: 'relative' }} ref={gaugeRef}>
        {isLoading && (
          <LoadingOverlay>
            <CircularProgress size={30} />
          </LoadingOverlay>
        )}
        {/* TODO: Replace with ECharts gauge implementation */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed #ccc',
            borderRadius: 2,
            backgroundColor: '#f9f9f9'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ color: gaugeColor, fontWeight: 'bold' }}>
              {value.toFixed(1)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Gauge visualization coming soon
            </Typography>
          </Box>
        </Box>
      </Box>
      <GaugeValue variant="h5">
        {value.toFixed(1)} {unit}
      </GaugeValue>
      <GaugeOptimalRange>
        Optimal range: {optStart.toFixed(1)} - {optEnd.toFixed(1)} {unit}
      </GaugeOptimalRange>
      {lastUpdated && (
        <LastUpdatedText>
          {formattedLastUpdated}
        </LastUpdatedText>
      )}
    </GaugeContainer>
  );
};

export default WaterQualityGauge;
